// =============================================================================
// GapVoiceInterview — "talk it through": the two-way voice interview on a gap
//
// The owner's spec, verbatim (W22, 2026-07-24): "the address this needs to be
// a two-way voice interview interaction as an option". Meira asks the gap
// question out loud, he answers out loud, she can follow up — and what he SAID
// lands in the same commit path a typed answer lands in.
//
// REUSE, NOT A SECOND ENGINE: this drives the app's EXISTING live-agent loop —
// `useLivingConversation` (packages/voice-client) minted through
// `startAgentSession` (POST /api/agent-session), exactly as Refine.tsx's orb
// does. The one difference is the anchor: `{ anchorType: 'gap', gap: { text,
// jobId } }`, a shape the backend has already supported since the session
// route was written (apps/api/routes/agent-session.routes.ts — "'gap' points
// the trusted-friend session at a live job-application gap (the 'talk it
// through' entry from the gap analysis)"). This lane is the client half that
// route was waiting for. No new conversation engine, no new transport, no new
// route.
//
// NO INVENTION: Meira's lines render in the transcript and are never part of
// the answer. The capture is assembled by `spokenUserAnswer` (user turns only)
// and re-checked by `guardSpokenCommit` before the confirm step is even
// offered — see lib/gapAnswerMode.ts for the full boundary. The user's own
// transcribed words, repaired only toward KNOWN terms and shown to him
// editable, are what get sent.
//
// SPLIT ON PURPOSE: `GapVoiceInterviewView` is presentational (every state
// driven by props) so the style guide renders the REAL component in all five
// states (hard rule #5); `GapVoiceInterview` is the container that wires the
// live hook to it.
// =============================================================================

import { useLivingConversation } from '@careeropt/voice-client';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  type GapAnswerMode,
  type SpokenTurn,
  type TalkAvailability,
  type VoiceLoopState,
  describeGapAnswerMode,
  describeTalkError,
  describeVoiceLoopState,
  deriveVoiceLoopState,
  guardSpokenCommit,
  isLive,
  resolveTalkAvailability,
  spokenUserAnswer,
} from '../../lib/gapAnswerMode';
import { type TermCorrection, describeCorrection, repairTerms } from '../../lib/termRepair';
import { startAgentSession } from '../../lib/api';
import { Orb } from '../Orb';
import styles from './GapAddressPanel.module.css';

/** Map the loop state onto the orb's own vocabulary, so the live component
 *  renders identically here and in the style guide. */
function orbPropsFor(state: VoiceLoopState): {
  status: 'disconnected' | 'connecting' | 'connected';
  mode: 'speaking' | 'listening';
  isSpeaking: boolean;
} {
  switch (state) {
    case 'connecting':
      return { status: 'connecting', mode: 'listening', isSpeaking: false };
    case 'listening':
      return { status: 'connected', mode: 'listening', isSpeaking: false };
    case 'agent_speaking':
      return { status: 'connected', mode: 'speaking', isSpeaking: true };
    default:
      return { status: 'disconnected', mode: 'listening', isSpeaking: false };
  }
}

export interface GapVoiceInterviewViewProps {
  state: VoiceLoopState;
  /** The whole conversation so far — Meira's lines included, DISPLAY ONLY. */
  turns: readonly SpokenTurn[];
  /** Every term-repair the transcript went through, shown so nothing is silent. */
  corrections: readonly TermCorrection[];
  /** Why the live loop is unavailable, when it is. */
  availability: TalkAvailability;
  /** The honest failure message, already humanised. */
  error: string | null;
  /** Live frequency getter for the orb; omitted in static previews. */
  frequencies?: () => { input: Uint8Array; output: Uint8Array };
  onStart: () => void;
  onStop: () => void;
  onUseAnswer: () => void;
  onRevertCorrection: (correction: TermCorrection) => void;
  /** Offered on the error state — never a dead end. */
  onRetry: () => void;
}

export function GapVoiceInterviewView({
  state,
  turns,
  corrections,
  availability,
  error,
  frequencies,
  onStart,
  onStop,
  onUseAnswer,
  onRevertCorrection,
  onRetry,
}: GapVoiceInterviewViewProps) {
  const orb = orbPropsFor(state);
  const live = isLive(state);
  // The guard runs HERE too, not only at send: the confirm action is never
  // even enabled over something he did not say.
  const guard = guardSpokenCommit(spokenUserAnswer(turns), turns);

  return (
    <div className={styles.voicePane}>
      <div className={styles.voiceHeadRow}>
        <Orb {...orb} frequencies={live ? frequencies : undefined} />
        <div className={styles.voiceStatus}>
          <span className={styles.voiceStatusLine}>{describeVoiceLoopState(state)}</span>
          {state === 'idle' ? (
            <span className={styles.voiceHint}>{describeGapAnswerMode('talk').hint}</span>
          ) : null}
        </div>
      </div>

      {state === 'unsupported' && !availability.available ? (
        <div className={styles.degradeNote}>{availability.explanation}</div>
      ) : null}

      {state === 'error' ? <div className={styles.errorNote}>{error}</div> : null}

      {turns.length > 0 ? (
        <div className={styles.voiceTranscript}>
          {turns.map((t, i) => (
            <div key={i} className={t.role === 'agent' ? styles.turnMira : styles.turnUser}>
              {t.text}
            </div>
          ))}
        </div>
      ) : null}

      {corrections.length > 0 ? (
        <div className={styles.repairStrip}>
          <span className={styles.repairLead}>
            I fixed what the mic misheard, against words already known. Undo anything I got wrong —
            your version wins.
          </span>
          {corrections.map((c, i) => (
            <span key={`${c.replacement}-${i}`} className={styles.repairItem}>
              {describeCorrection(c)}
              <button
                type="button"
                className={styles.repairUndo}
                onClick={() => onRevertCorrection(c)}
              >
                undo
              </button>
            </span>
          ))}
        </div>
      ) : null}

      <div className={styles.actionRow}>
        {state === 'idle' ? (
          <button type="button" className={styles.primaryButton} onClick={onStart}>
            Start talking
          </button>
        ) : null}
        {live ? (
          <button type="button" className={styles.secondaryButton} onClick={onStop}>
            {"I'm done"}
          </button>
        ) : null}
        {state === 'ready_to_confirm' ? (
          <>
            <button
              type="button"
              className={styles.primaryButton}
              onClick={onUseAnswer}
              disabled={!guard.ok}
            >
              Use what I said
            </button>
            <button type="button" className={styles.secondaryButton} onClick={onStart}>
              Keep talking
            </button>
          </>
        ) : null}
        {state === 'error' ? (
          <button type="button" className={styles.secondaryButton} onClick={onRetry}>
            Try again
          </button>
        ) : null}
      </div>

      {state === 'ready_to_confirm' && !guard.ok ? (
        <div className={styles.errorNote}>{guard.message}</div>
      ) : null}
    </div>
  );
}

export interface GapVoiceInterviewProps {
  jobId: string;
  /** The gap being addressed — the session's SUBJECT, sent as the anchor. */
  gapText: string;
  /** His own corpus vocabulary for this gap, served by gap-address/start. */
  corpusTerms: readonly string[];
  /** Hands the confirmed capture (his words) back to the panel's send path. */
  onAnswerReady: (answer: string) => void;
}

/** Read the live browser environment once, outside render. */
function readTalkEnvironment(): TalkAvailability {
  const hasMediaDevices =
    typeof navigator !== 'undefined' &&
    typeof navigator.mediaDevices?.getUserMedia === 'function';
  const isSecureContext = typeof window === 'undefined' ? true : window.isSecureContext !== false;
  return resolveTalkAvailability({ hasMediaDevices, isSecureContext });
}

export function GapVoiceInterview({
  jobId,
  gapText,
  corpusTerms,
  onAnswerReady,
}: GapVoiceInterviewProps) {
  const [availability] = useState<TalkAvailability>(readTalkEnvironment);
  const [turns, setTurns] = useState<SpokenTurn[]>([]);
  const [corrections, setCorrections] = useState<TermCorrection[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [ended, setEnded] = useState(false);
  const mintRef = useRef(startAgentSession);
  const termsRef = useRef(corpusTerms);
  termsRef.current = corpusTerms;

  const conversation = useLivingConversation(mintRef.current, {
    onCaption: ({ source, text }) => {
      const line = (text ?? '').trim();
      if (!line) return;
      if (source === 'agent') {
        // Meira's words. Rendered, never captured — see gapAnswerMode.ts.
        setTurns((prev) => [...prev, { role: 'agent', text: line }]);
        return;
      }
      // HIS words: repaired ONLY toward known terms, every change reported.
      const report = repairTerms(line, { corpusTerms: termsRef.current });
      if (report.corrections.length > 0) {
        setCorrections((prev) => [...prev, ...report.corrections]);
      }
      setTurns((prev) => [...prev, { role: 'user', text: report.corrected }]);
    },
    onError: (message) => setError(describeTalkError(message)),
  });

  const state = deriveVoiceLoopState({
    supported: availability.available,
    error,
    ended,
    hasUserSpeech: Boolean(spokenUserAnswer(turns)),
    status: conversation.status,
    isSpeaking: conversation.isSpeaking,
  });

  // Release the mic if the panel unmounts mid-session.
  const stopRef = useRef(conversation.stop);
  stopRef.current = conversation.stop;
  useEffect(() => () => stopRef.current(), []);

  const handleStart = useCallback(() => {
    if (!availability.available) return;
    setError(null);
    setEnded(false);
    conversation
      .start({ anchorType: 'gap', gap: { text: gapText, jobId } })
      .then((id) => {
        if (id === null) {
          setError(describeTalkError('connect failed'));
        }
      })
      .catch((err: unknown) => {
        setError(describeTalkError(err instanceof Error ? err.message : ''));
      });
  }, [availability.available, conversation, gapText, jobId]);

  const handleStop = useCallback(() => {
    conversation.stop();
    setEnded(true);
  }, [conversation]);

  const handleUseAnswer = useCallback(() => {
    const assembled = spokenUserAnswer(turns);
    const guard = guardSpokenCommit(assembled, turns);
    // Fail-closed: an unguarded capture never leaves this component.
    if (!guard.ok) {
      setError(guard.message);
      return;
    }
    onAnswerReady(guard.capture);
  }, [onAnswerReady, turns]);

  const handleRevertCorrection = useCallback((correction: TermCorrection) => {
    // Undo the swap in the transcript itself, so what he reviews (and what the
    // capture is assembled from) is the raw heard wording again.
    setTurns((prev) =>
      prev.map((t) =>
        t.role === 'user' && t.text.includes(correction.replacement)
          ? { ...t, text: t.text.replace(correction.replacement, correction.heard) }
          : t,
      ),
    );
    setCorrections((prev) => prev.filter((c) => c !== correction));
  }, []);

  const frequencies = useMemo(() => conversation.frequencies, [conversation.frequencies]);

  return (
    <GapVoiceInterviewView
      state={state}
      turns={turns}
      corrections={corrections}
      availability={availability}
      error={error}
      frequencies={frequencies}
      onStart={handleStart}
      onStop={handleStop}
      onUseAnswer={handleUseAnswer}
      onRevertCorrection={handleRevertCorrection}
      onRetry={handleStart}
    />
  );
}

/** Re-exported for the panel's mode row so the labels have one home. */
export function talkModeLabel(mode: GapAnswerMode): string {
  return describeGapAnswerMode(mode).label;
}
